import { useQuery } from '@tanstack/react-query'
import { adminDiagnosticsApi } from '@/api/adminDiagnostics'
import type { DiagnosticAssignmentRow, DiagnosticResultResponse } from '@/api/adminDiagnostics'

export type LevelDiagnosticView =
  | { state: 'none' | 'incomplete' | 'levelPending' }
  | { state: 'ready'; score: number; correct: number; total: number }

/**
 * 원생의 진단 배정 중 "대표" 1건을 고른다.
 * COMPLETED 우선 → 그 안에서 roundNo 최대(동률 시 첫 매치). COMPLETED가 없으면 전체 중 roundNo 최대(미완료 표시용).
 * 대상 userId 배정이 없으면 null.
 *
 * cf. `components/admin/diagnostics/diagnosticRep.ts`의 `pickDiagnosticRep`는 active 우선(운영 셀용)으로
 * 의도적으로 우선순위가 다르다. 여기는 "채점 완료된 점수 표시"가 목적이라 COMPLETED 우선이다.
 */
export function resolveRepAssignment(
  rows: DiagnosticAssignmentRow[],
  userId: number,
): DiagnosticAssignmentRow | null {
  const mine = rows.filter((r) => r.userId === userId)
  if (mine.length === 0) return null
  const completed = mine.filter((r) => r.status === 'COMPLETED')
  const pool = completed.length > 0 ? completed : mine
  return pool.reduce((best, r) => (r.roundNo > best.roundNo ? r : best), pool[0])
}

/** 대표 assignment + 결과를 표시용 view 상태로 매핑한다. */
export function toLevelView(
  rep: DiagnosticAssignmentRow | null,
  result: DiagnosticResultResponse | null,
): LevelDiagnosticView {
  if (!rep) return { state: 'none' }
  if (rep.status !== 'COMPLETED') return { state: 'incomplete' }
  if (!result || result.level == null) return { state: 'levelPending' }
  return { state: 'ready', score: result.level.score, correct: result.level.correct, total: result.level.total }
}

export function useLevelDiagnosticResult(userId: number) {
  return useQuery({
    queryKey: ['lc', 'admin', 'levelResult', userId],
    enabled: Number.isInteger(userId) && userId > 0,
    retry: false, // 403(ENTRANCE_TEST 미보유) retry storm 방지 → 컴포넌트가 isError→null 렌더(fail-soft)
    staleTime: 30_000,
    queryFn: async (): Promise<LevelDiagnosticView> => {
      const rows = (await adminDiagnosticsApi.list()).data.data ?? []
      const rep = resolveRepAssignment(rows, userId)
      const result =
        rep && rep.status === 'COMPLETED' ? (await adminDiagnosticsApi.getResult(rep.id)).data.data : null
      return toLevelView(rep, result)
    },
  })
}
